import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard, NoAuthGuard } from '@core/auth';
import { PermisosUsuario } from '@core/types';
import { MultiFaGuard, PermissionsGuard } from '@core/yalo/guards';
import { MultiFaResolver } from '@core/yalo/resolvers';
import { LayoutComponent } from './layout/layout.component';

const routes: Routes = [
  // Redirect empty path to '/dashboard'
  { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
  // Redirect signed in user to the '/dashboard'
  // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  // { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },
  {
    path: 'sign-up',
    loadChildren: () => import('@auth/sign-up').then((m) => m.SignUpPageModule),
  },
  {
    path: 'error',
    loadChildren: () =>
      import('./pages/utils/error/error.module').then((m) => m.ErrorPageModule),
  },
  {
    path: 'sign-up-successful/:method',
    loadChildren: () =>
      import('@auth/sign-up-successful').then(
        (m) => m.SignUpSuccessfulPageModule
      ),
  },
  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: { layout: 'empty' },
    children: [
      {
        path: 'sign-in',
        loadChildren: () =>
          import('@auth/sign-in').then((m) => m.SignInPageModule),
      },
    ],
  },
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: { layout: 'empty' },
    children: [
      {
        path: 'documentos/aprobar',
        loadChildren: () =>
          import('@pages/documentos/aprobacion-documentos').then(
            (m) => m.AprobacionDocumentosModule
          ),
      },
      {
        path: 'orgs-board',
        canActivate: [MultiFaGuard],
        canActivateChild: [MultiFaGuard],
        loadChildren: () =>
          import('@auth/orgs-board').then((m) => m.OrgsBoardPageModule),
      },
      {
        path: 'multi-fa',
        //canActivate: [NoFaGuard],
        resolve: {
          multiFaAuth: MultiFaResolver,
        },
        loadChildren: () =>
          import('@auth/multi-fa').then((m) => m.MultiFaPageModule),
      },
      {
        path: 'vincular',
        loadChildren: () =>
          import('./pages/organizacion/vincular/vincular.module').then(
            (m) => m.VincularPageModule
          ),
      },
      {
        path: 'renew',
        loadChildren: () =>
          import('@auth/renew').then((m) => m.RenewPageModule),
      },
      {
        path: 'suscripcion',
        loadChildren: () =>
          import('@organizacion/suscripcion').then(
            (m) => m.SuscripcionPageModule
          ),
      },
      // Editor de Mapa de Mesas
      {
        path: 'configuraciones/mapa-mesas/editor/:CodEstablecimiento/:CodMapa',
        loadChildren: () => import('./pages/configuraciones/mapa-mesas/editor/mapa-mesas-editor.module').then(m => m.MapaMesasEditorPageModule)
      }
    ],
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard, MultiFaGuard],
    canActivateChild: [AuthGuard, MultiFaGuard],
    children: [
      {
        path: 'dashboard',
        canActivate: [PermissionsGuard],
        data: { CodPermiso: PermisosUsuario.estadisticas_informes },
        loadChildren: () =>
          import('@pages/dashboard').then((m) => m.DashboardPageModule),
      },
      // Clientes
      {
        path: 'clientes',
        canActivate: [PermissionsGuard],
        data: { CodPermiso: PermisosUsuario.manejo_clientes },
        loadChildren: () =>
          import('@pages/clientes/clientes').then((m) => m.ClientesModule),
      },
      {
        path: 'configuraciones-cliente',
        canActivate: [PermissionsGuard],
        data: { CodPermiso: PermisosUsuario.manejo_clientes },
        loadChildren: () =>
          import('@pages/clientes/configuraciones-clientes/configuraciones-clientes.module').then(
            (m) => m.ConfiguracionesClientesModule
          ),
      },
      {
        path: 'bitacoras',
        canActivate: [PermissionsGuard],
        data: { CodPermiso: PermisosUsuario.vista_bitacoras },
        loadChildren: () =>
          import('./pages/bitacoras/bitacoras.module').then(
            (m) => m.BitacorasPageModule
          ),
      },
      {
        path: 'organizacion',
        children: [
          {
            path: 'configuracion',
            loadChildren: () =>
              import(
                './pages/organizacion/configuracion/configuracion.module'
              ).then((m) => m.ConfiguracionPageModule),
          },
          {
            path: 'crear',
            loadChildren: () =>
              import('./pages/organizacion/crear/crear.module').then(
                (m) => m.CrearPageModule
              ),
          },
          {
            path: 'establecimiento-detalle',
            loadChildren: () =>
              import(
                '@pages/organizacion/configuracion/components/establecimiento-detalle'
              ).then((m) => m.EstablecimientoDetalleModule),
          },
        ],
      },
      {
        path: 'catalogo',
        canActivate: [PermissionsGuard],
        data: { CodPermiso: PermisosUsuario.gestion_catalogo },
        children: [
          {
            path: 'productos',
            loadChildren: () =>
              import('@pages/productos/productos').then(
                (m) => m.ProductosPageModule
              ),
          },
          {
            path: 'servicios',
            loadChildren: () =>
              import('./pages/productos/servicios/servicios.module').then(
                (m) => m.ServiciosPageModule
              ),
          },
          {
            path: 'productos-compuestos',
            loadChildren: () =>
              import('@pages/productos/productos-compuestos').then(
                (m) => m.ProductosCompuestosPageModule
              ),
          },
          {
            path: 'grupo-productos',
            loadChildren: () =>
              import(
                '@pages/productos/grupo-productos/grupo-productos.module'
              ).then((m) => m.GrupoProductosModule),
          },
          {
            path: 'materia-prima',
            loadChildren: () =>
              import('@pages/productos/materia-prima').then(
                (m) => m.MateriaPrimaPageModule
              ),
          },
          {
            path: 'configuracion-productos',
            loadChildren: () =>
              import('@pages/productos/configuracion-productos').then(
                (m) => m.ConfiguracionProductosPageModule
              ),
          },
        ],
      },
      {
        path: 'inventario',
        children: [
          {
            canActivate: [PermissionsGuard],
            data: { CodPermiso: PermisosUsuario.manejo_inventario },
            path: 'bodegas',
            loadChildren: () =>
              import('@pages/inventario/bodegas').then((m) => m.BodegasModule),
          },
          {
            canActivate: [PermissionsGuard],
            data: { CodPermiso: PermisosUsuario.gestion_compras },
            path: 'compras',
            loadChildren: () =>
              import('@pages/inventario/compras').then((m) => m.ComprasModule),
          },
          {
            canActivate: [PermissionsGuard],
            data: { CodPermiso: PermisosUsuario.manejo_inventario },
            path: 'gestion-inventario',
            loadChildren: () =>
              import('@pages/inventario/gestion-inventario').then(
                (m) => m.GestionInventarioModule
              ),
          },
          {
            canActivate: [PermissionsGuard],
            data: { CodPermiso: PermisosUsuario.manejo_inventario },
            path: 'proveedores',
            loadChildren: () =>
              import('@pages/inventario/proveedores').then(
                (m) => m.ProveedoresModule
              ),
          },
        ],
      },
      {
        path: 'documentos',
        children: [
          {
            path: 'requisiciones',
            loadChildren: () =>
              import('@pages/documentos/requisiciones').then(
                (m) => m.RequisicionesModule
              ),
          },
          {
            path: 'ordenes-compra',
            loadChildren: () =>
              import('@pages/documentos/ordenes-compra').then(
                (m) => m.OrdenesCompraModule
              ),
          },
          {
            path: 'facturas-compra',
            loadChildren: () =>
            import('@pages/documentos/facturas-compra').then(
              (m) => m.FacturasCompraModule
            ),
          }
        ],
      },
      {
        path: 'colaboradores',
        canActivate: [PermissionsGuard],
        data: { CodPermiso: PermisosUsuario.manejo_colaboradores },
        loadChildren: () =>
          import('@pages/colaboradores').then((m) => m.ColaboradoresModule),
      },
      {
        path: 'cuenta',
        loadChildren: () =>
          import('./pages/cuenta/cuenta.module').then(
            (m) => m.CuentaPageModule
          ),
      },
      {
        path: 'tables',
        loadChildren: () =>
          import('./tables/tables.module').then((m) => m.TablesModule),
      },
      {
        path: 'configuraciones',
        children: [
          {
            path: 'ajustes-basicos',
            canActivate: [PermissionsGuard],
            data: { CodPermiso: PermisosUsuario.ajustes_basicos },
            loadChildren: () =>
              import('@pages/configuraciones/ajustes-basicos').then(
                (m) => m.AjustesBasicosModule
              ),
          },
          {
            path: 'mapa-mesas',
            // canActivate: [PermissionsGuard],
            // data: { CodPermiso: PermisosUsuario.configuracion_general },
            loadChildren: () =>
              import(
                '@pages/configuraciones/mapa-mesas/mapa-mesas.module'
              ).then((m) => m.MapaMesasPageModule),
          },
          {
            path: 'inventario-pro',
            // canActivate: [PermissionsGuard],
            // data: { CodPermiso: PermisosUsuario.configuracion_general },
            loadChildren: () =>
              import(
                '@pages/configuraciones/configuracion-inventario-pro/configuracion-inventario-pro.module'
              ).then((m) => m.ConfiguracionInventarioProModule),
          },
          {
            path: 'monitores',
            // canActivate: [PermissionsGuard],
            // data: { CodPermiso: PermisosUsuario.configuracion_general },
            loadChildren: () =>
              import('@pages/configuraciones/monitores/monitores.module').then(
                (m) => m.MonitoresModule
              ),
          },
          {
            path: 'configuraciones-caja',
            canActivate: [PermissionsGuard],
            data: { CodPermiso: PermisosUsuario.configuracion_caja },
            loadChildren: () =>
              import('@pages/configuraciones/configuraciones-caja').then(
                (m) => m.ConfiguracionesCajaPageModule
              ),
          },
          {
            path: 'integraciones',
            canActivate: [PermissionsGuard],
            data: { CodPermiso: PermisosUsuario.integraciones },
            loadChildren: () =>
              import('@pages/configuraciones/integraciones').then(
                (m) => m.IntegracionesPageModule
              ),
          },
        ],
      },
      {
        path: 'notificaciones',
        loadChildren: () =>
          import('@pages/notificaciones').then(
            (m) => m.NotificacionesPageModule
          ),
      },
      {
        path: 'acceso-denegado',
        loadComponent: () =>
          import('@pages/no-access').then((c) => c.NoAccessPage),
      },
    ],
  },
  {
    path: 'recuperar-password/:codUsuario/:codToken/:token',
    // component: LayoutComponent,
    loadChildren: () =>
      import(
        './shared/seguridad/recuperar-password/recuperar-password.module'
      ).then((m) => m.RecuperarPasswordModule),
  },
  {
    path: 'verificar-correo/:codUsuario/:codVerificacionUsuario/:token',
    loadChildren: () =>
      import(
        './shared/seguridad/verificar-correo/verificar-correo.module'
      ).then((m) => m.VerificarCorreoPageModule),
  },
  // widlcard route for redirecting to the 404 page
  // { path: '**', redirectTo: 'dashboard' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true })],
  exports: [RouterModule],
})
export class AppRoutingModule { }
